import {
  AdjustmentsHorizontalIcon,
  ArrowRightStartOnRectangleIcon,
  BeakerIcon,
  BoltIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ChartPieIcon,
  CheckCircleIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  CogIcon,
  CommandLineIcon,
  CurrencyDollarIcon,
  HomeIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import OrganizationSelector, { Organization } from "./OrganizationSelector";
import { CreateOrganizationFlow } from "./organization/CreateOrganizationFlow";
import { usePersistedPreference } from "../hooks/useFilterPersistence";

const navigation = [
  { name: "Dashboard", href: "/", icon: HomeIcon },
  { name: "Strategy", href: "/strategy", icon: LightBulbIcon },
  { name: "Objectives", href: "/okrs", icon: CheckCircleIcon },
  { name: "Segments", href: "/segments", icon: ChartPieIcon },
  { name: "Propositions", href: "/propositions", icon: BriefcaseIcon },
  {
    name: "Positioning",
    href: "/positioning",
    icon: AdjustmentsHorizontalIcon,
  },
  { name: "Companies", href: "/companies", icon: BuildingOfficeIcon },
  { name: "Departments", href: "/areas", icon: UserGroupIcon },
  { name: "Gravity Plans", href: "/gravity-plans", icon: RocketLaunchIcon },
];

const toolsNavigation = [
  { name: "Research Library", href: "/research", icon: BeakerIcon },
  { name: "AI Assistant", href: "/research/assistant", icon: SparklesIcon },
];

const adminNavigation = [
  { name: "Admin Dashboard", href: "/admin", icon: ChartBarIcon },
  {
    name: "Manage Organizations",
    href: "/admin/organizations",
    icon: BuildingOfficeIcon,
  },
  { name: "Manage Users", href: "/admin/users", icon: UserGroupIcon },
  {
    name: "Super Admin Management",
    href: "/admin/super-admins",
    icon: ShieldCheckIcon,
  },
  { name: "System Settings", href: "/admin/system", icon: CogIcon },
  {
    name: "Billing Management",
    href: "/admin/billing",
    icon: CurrencyDollarIcon,
  },
  {
    name: "Webhook Events",
    href: "/admin/webhooks",
    icon: BoltIcon,
  },
];

interface SidebarProps {
  user: any;
  orgs: Organization[];
  selectedOrg: Organization | null;
  userOrgRole: string | null;
  onOrgChange: (org: Organization) => void;
  onOrganizationCreated: (org: Organization) => void;
  onLogout: () => void;
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  user,
  orgs,
  selectedOrg,
  userOrgRole,
  onOrgChange,
  onOrganizationCreated,
  onLogout,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [isCollapsed, setIsCollapsed] = usePersistedPreference(
    "sidebar.collapsed",
    false,
    "ui",
  );

  return (
    <div
      className={`flex flex-col ${isCollapsed ? "w-16" : "w-64"} bg-gray-800 h-full transition-all duration-300 ease-in-out`}
    >
      <div
        className={`flex items-center ${isCollapsed ? "justify-center" : "justify-between"} h-16 bg-gray-900 px-2`}
      >
        {!isCollapsed && (
          <h1 className="text-white text-2xl font-bold flex items-center gap-2">
            <LightBulbIcon className="h-6 w-6 flex-shrink-0" />
            <span>Spotlight</span>
          </h1>
        )}
        {isCollapsed && (
          <LightBulbIcon className="h-6 w-6 text-white flex-shrink-0" />
        )}
        {onNavigate && !isCollapsed && (
          <button
            onClick={onNavigate}
            className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 lg:hidden"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
        {/* Desktop collapse toggle button */}
        {!isCollapsed && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex p-1 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
            title="Collapse sidebar"
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {/* Collapsed state toggle - positioned below the logo */}
      {isCollapsed && (
        <div className="hidden lg:flex justify-center px-2 pb-4">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
            title="Expand sidebar"
          >
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Organization Selector */}
      {user && !isCollapsed && (
        <div className="px-4 py-4 border-b border-gray-700">
          {orgs.length > 0 ? (
            <OrganizationSelector
              value={selectedOrg}
              onChange={onOrgChange}
              orgs={orgs}
              onOrganizationCreated={onOrganizationCreated}
            />
          ) : (
            <button
              className="w-full px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 hover:text-white hover:border-gray-500 transition-colors"
              onClick={() => setShowCreateOrgDialog(true)}
            >
              + Add Organization
            </button>
          )}
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            onClick={onNavigate}
            title={isCollapsed ? item.name : ""}
            className={({ isActive }) =>
              `flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium rounded-md group relative ${
                isActive
                  ? "bg-gray-900 text-white"
                  : "text-gray-300 hover:bg-gray-700 hover:text-white"
              }`
            }
          >
            <item.icon
              className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
            />
            {!isCollapsed && <span>{item.name}</span>}
            {/* Tooltip for collapsed mode */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                {item.name}
              </div>
            )}
          </NavLink>
        ))}

        {/* Tools Section - Only show for editors and above */}
        {userOrgRole && userOrgRole !== "viewer" && (
          <div className="mt-8 pt-4">
            {!isCollapsed && (
              <h3 className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Tools
              </h3>
            )}
            <div className="mt-2 space-y-1">
              {toolsNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onNavigate}
                  title={isCollapsed ? item.name : ""}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium rounded-md group relative ${
                      isActive
                        ? "bg-gray-900 text-white"
                        : "text-gray-300 hover:bg-gray-700 hover:text-white"
                    }`
                  }
                >
                  <item.icon
                    className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
                  />
                  {!isCollapsed && <span>{item.name}</span>}
                  {/* TODO: Add GenAI access check and show lock icon if no access */}
                  {/* Tooltip for collapsed mode */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                      {item.name}
                    </div>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        {/* Admin Section - Only show for super admins */}
        {user?.role === "super_admin" && (
          <div className="mt-8 pt-4">
            {!isCollapsed && (
              <h3 className="px-2 text-xs font-semibold text-red-400 uppercase tracking-wider">
                Super Admin
              </h3>
            )}
            <div className="mt-2 space-y-1">
              {adminNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onNavigate}
                  title={isCollapsed ? item.name : ""}
                  className={({ isActive }) =>
                    `flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium rounded-md group relative ${
                      isActive
                        ? "bg-red-900 text-white"
                        : "text-gray-300 hover:bg-red-700 hover:text-white"
                    }`
                  }
                >
                  <item.icon
                    className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
                  />
                  {!isCollapsed && <span>{item.name}</span>}
                  {/* Tooltip for collapsed mode */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                      {item.name}
                    </div>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Bottom Section - User Info & Management */}
      {user && (
        <div className="border-t border-gray-700">
          {/* User Info Header */}
          {!isCollapsed && (
            <div className="px-4 py-3 bg-gray-900/50">
              <div className="flex items-center">
                <UserCircleIcon className="h-8 w-8 text-gray-400 flex-shrink-0" />
                <div className="ml-3 flex-1 overflow-hidden">
                  <p className="text-sm font-medium text-gray-300 truncate">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                      : user.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {userOrgRole
                      ? `${userOrgRole.charAt(0).toUpperCase() + userOrgRole.slice(1)}`
                      : "Member"}
                    {selectedOrg && ` at ${selectedOrg.name}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Management Links */}
          <div className="px-2 py-2 space-y-1">
            <button
              className={`w-full flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group relative`}
              title={isCollapsed ? "My Tasks" : ""}
              onClick={() => {
                navigate("/my-tasks");
                onNavigate?.();
              }}
            >
              <ClipboardDocumentListIcon
                className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
              />
              {!isCollapsed && <span>My Tasks</span>}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                  My Tasks
                </div>
              )}
            </button>

            <button
              className={`w-full flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group relative`}
              title={isCollapsed ? "Claude Instructions" : ""}
              onClick={() => {
                navigate("/claude-instructions");
                onNavigate?.();
              }}
            >
              <CommandLineIcon
                className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
              />
              {!isCollapsed && <span>Claude Instructions</span>}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                  Claude Instructions
                </div>
              )}
            </button>

            <button
              className={`w-full flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group relative`}
              title={isCollapsed ? "Account Management" : ""}
              onClick={() => {
                navigate("/account");
                onNavigate?.();
              }}
            >
              <UserCircleIcon
                className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
              />
              {!isCollapsed && <span>Account Management</span>}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                  Account Management
                </div>
              )}
            </button>

            {selectedOrg && (
              <button
                className={`w-full flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group relative`}
                title={isCollapsed ? "Organization Management" : ""}
                onClick={() => {
                  navigate("/organization");
                  onNavigate?.();
                }}
              >
                <Cog6ToothIcon
                  className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
                />
                {!isCollapsed && <span>Organization Management</span>}
                {isCollapsed && (
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                    Organization Management
                  </div>
                )}
              </button>
            )}

            <button
              className={`w-full flex items-center ${isCollapsed ? "justify-center" : ""} px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white group relative`}
              title={isCollapsed ? "Log Out" : ""}
              onClick={onLogout}
            >
              <ArrowRightStartOnRectangleIcon
                className={`h-6 w-6 ${isCollapsed ? "" : "mr-3"} flex-shrink-0`}
              />
              {!isCollapsed && <span>Log Out</span>}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10">
                  Log Out
                </div>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Create Organization Dialog */}
      <CreateOrganizationFlow
        open={showCreateOrgDialog}
        onClose={() => setShowCreateOrgDialog(false)}
        onOrganizationCreated={(newOrg) => {
          setShowCreateOrgDialog(false);
          if (onOrganizationCreated) {
            onOrganizationCreated(newOrg);
          }
        }}
      />
    </div>
  );
};

export default Sidebar;
